/**
 * Programs Service
 * Handles API calls for programs management
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';

export interface Program {
  programID: number;
  programName: string;
  description: string;
  grantID?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Get all active programs
 */
export async function getPrograms(): Promise<Program[]> {
  const response = await apiGet<Program[]>('/programs');

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load programs. Please try again.');
  }

  return response.data || [];
}

/**
 * Create a new program
 */
export async function createProgram(data: {
  programName: string;
  description?: string;
  grantID?: number;
}): Promise<Program> {
  const response = await apiPost<Program>('/programs', data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to create program. Please try again.');
  }

  return response.data;
}

/**
 * Update a program
 */
export async function updateProgram(
  id: number,
  data: {
    programName: string;
    description?: string;
    grantID?: number;
  }
): Promise<Program> {
  const response = await apiPut<Program>(`/programs/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update program. Please try again.');
  }

  return response.data;
}

/**
 * Delete a program (soft delete)
 */
export async function deleteProgram(id: number): Promise<void> {
  const response = await apiDelete<void>(`/programs/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to delete program. Please try again.');
  }
}
